import React, { type MouseEvent, useCallback } from 'react';

import { di } from 'react-magnetic-di';
import { matchRoute, useRouter } from 'react-resource-router-next';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { LinkMenuItem } from '@atlaskit/navigation-system';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useGeneratePath, useTransformStudioRoute } from '@atlassian/atlassian-studio-routing';

import type { SideNavItem } from '../../types';

const boxStyles = cssMap({
	root: {
		paddingInlineStart: token('space.150'),
	},
});

type SideNavMenuItemProps = {
	item: SideNavItem;
	onClick?: (analyticsEvent: UIAnalyticsEvent) => void;
};

const slugify = (label: string) => {
	// Cannot use `.replaceAll` due to the following error in pipelines:
	// Property 'replaceAll' does not exist on type 'string'. Do you need to change your target library? Try changing the 'lib' compiler option to 'es2021' or later.
	return label.trim().toLowerCase().split(' ').join('-');
};

export const SideNavMenuItem = ({ item, onClick }: SideNavMenuItemProps): React.JSX.Element => {
	di(useRouter);

	const [routerStore, routerActions] = useRouter();
	const generatePath = useGeneratePath();
	const { transformRoute } = useTransformStudioRoute();

	const {
		label,
		Icon,
		route,
		matchRoutes,
		params,
		query,
		hashLink,
		isSubgroup,
		elemAfter,
		isDisabledOpacity,
	} = item;

	const transformedMatchRoutes = matchRoutes.map((matchRoute) => {
		const { route } = transformRoute(matchRoute);
		return route;
	});

	const isSelected =
		item.isSelected ??
		Boolean(matchRoute(transformedMatchRoutes, routerStore.location.pathname, undefined));

	const queryString = new URLSearchParams(query).toString();
	const path = `${generatePath(route.path, params)}${queryString && `?${queryString}`}${hashLink ? `#${hashLink}` : ''}`;

	const handleClick = useCallback(
		(event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			onClick?.(analyticsEvent);
			event.preventDefault();
			routerActions.push(path);
		},
		[onClick, routerActions, path],
	);
	const menuLinkItem = (
		<LinkMenuItem
			testId={`side-navigation-menu-item-${slugify(label)}`}
			elemBefore={
				<Icon label="" color={isDisabledOpacity ? token('color.icon.disabled') : 'currentColor'} />
			}
			href={path}
			isSelected={isSelected}
			onClick={handleClick}
			elemAfter={elemAfter}
		>
			<Text color={isDisabledOpacity ? 'color.text.disabled' : 'inherit'}>{label}</Text>
		</LinkMenuItem>
	);

	return isSubgroup ? <Box xcss={boxStyles.root}>{menuLinkItem}</Box> : menuLinkItem;
};
